import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from 'components/ErrorBoundary';
import { useAuthInit } from 'stores/authStore';
import { ROUTES } from 'utils/constants';
import ScrollToTopButton from 'components/ScrollToTopButton';
import PageTitle from 'components/PageTitle';
import CommandPalette from 'components/CommandPalette';
import {
  useCommandPaletteStore,
  useCommandPaletteShortcut,
} from 'stores/commandPaletteStore';
import { useCompanyAliasUrlSync } from 'hooks/useCompanyAliasUrlSync';
import RouteLoadingFallback from 'components/RouteLoadingFallback';
import RouteChunkErrorBoundary from 'components/RouteChunkErrorBoundary';
import { lazyRoute, lazyNamedRoute } from 'utils/lazyRoute';
import AppToaster from 'components/AppToaster';
import InstoreqrLoadingPage from 'pages/instoreqr/InstoreqrLoadingPage';
import { isInstoreqrPath } from 'pages/instoreqr/isInstoreqrPath';

const AuthenticatedRoutes = lazyRoute(
  () => import('components/AuthenticatedRoutes'),
);
const Home = lazyRoute(() => import('pages/home'));
const ShopManageLayout = lazyRoute(
  () => import('components/AuthenticatedRoutes/ShopManageLayout'),
);
const ManageShop = lazyRoute(() => import('pages/shop/manage/ManageShopPage'));
const OnlineShop = lazyRoute(() => import('pages/shop/OnlineShopPage'));
const ShopOrdersPage = lazyRoute(() => import('pages/shop/orders/OrdersPage'));
const ShopOrderDetailPage = lazyRoute(
  () => import('pages/shop/manage/OrderDetailPage'),
);
const GuestCartLayout = lazyRoute(
  () => import('pages/shop/components/ShopCartLayout'),
);
const GuestCart = lazyRoute(() => import('pages/shop/CartPage'));
const GuestCheckout = lazyRoute(() => import('pages/shop/CheckoutPage'));
const GuestOrders = lazyRoute(() => import('pages/shop/manage/OrdersPage'));
const ShopStorefront = lazyRoute(
  () => import('pages/shop/components/ShopPageLayout'),
);
const StorefrontProductList = lazyRoute(
  () => import('pages/shop/handle/ShopHandlePage'),
);
const StorefrontProductDetail = lazyRoute(
  () => import('pages/shop/product/ProductDetailPage'),
);
const StorefrontOrderSuccess = lazyRoute(
  () => import('pages/shop/orders/OrderSuccessPage'),
);
const StorefrontOrderHistory = lazyRoute(
  () => import('pages/shop/OrderHistoryPage'),
);
const StorefrontOrderDetail = lazyRoute(
  () => import('pages/shop/orders/OrderDetailPage'),
);
const PublicTaxCalculator = lazyRoute(() => import('pages/taxCalculator'));
const VerifyPosReceipt = lazyRoute(() => import('pages/verifyReceipt'));
const VerifyArInvoice = lazyRoute(() => import('pages/verifyArInvoice'));
const ReceiptImageTab = lazyRoute(() => import('pages/ReceiptImageTab'));
const AuthCallback = lazyNamedRoute(() => import('pages/auth'), 'AuthCallback');
const SupportPage = lazyRoute(() => import('pages/support'));
const ComingSoonStandalone = lazyRoute(
  () => import('components/ComingSoonStandalone/ComingSoonStandalone'),
);
const PrivacyPolicy = lazyNamedRoute(
  () => import('pages/legal'),
  'PrivacyPolicy',
);
const TermsAndConditions = lazyNamedRoute(
  () => import('pages/legal'),
  'TermsAndConditions',
);
const CookiePolicy = lazyNamedRoute(
  () => import('pages/legal'),
  'CookiePolicy',
);
const Disclaimer = lazyNamedRoute(() => import('pages/legal'), 'Disclaimer');
const NpcCertificateViewer = lazyNamedRoute(
  () => import('pages/legal'),
  'NpcCertificateViewer',
);
const EventRegistrationPage = lazyRoute(
  () => import('pages/events/public/EventRegistrationPage'),
);
const EventTicketPage = lazyRoute(
  () => import('pages/events/public/EventTicketPage'),
);
const EventScannerPage = lazyRoute(
  () => import('pages/events/public/EventScannerPage'),
);
const EventSurveyPage = lazyRoute(
  () => import('pages/events/public/EventSurveyPage'),
);
const CustomerMenu = lazyRoute(() => import('pages/instoreqr/CustomerMenu'));
const PublicReserveLayout = lazyRoute(
  () => import('pages/public/reserve/PublicReserveLayout'),
);
const PublicPlacesList = lazyRoute(
  () => import('pages/public/reserve/PublicPlacesList'),
);
const PublicPlaceBook = lazyRoute(
  () => import('pages/public/reserve/PublicPlaceBook'),
);
const PublicConfirmation = lazyRoute(
  () => import('pages/public/reserve/PublicConfirmation'),
);
const PublicLookup = lazyRoute(
  () => import('pages/public/reserve/PublicLookup'),
);
const PublicReserveCameraCapture = lazyRoute(
  () => import('pages/public/reserve/PublicReserveCameraCapture'),
);
const PublicReserveAuthorizedScanCapture = lazyRoute(
  () => import('pages/public/reserve/PublicReserveAuthorizedScanCapture'),
);

function AppSuspenseFallback() {
  const { pathname } = useLocation();
  if (isInstoreqrPath(pathname)) {
    return <InstoreqrLoadingPage />;
  }
  return <RouteLoadingFallback variant="full" />;
}

function App() {
  useAuthInit();
  useCommandPaletteShortcut();
  useCompanyAliasUrlSync();

  const location = useLocation();
  const isReceiptImageOnlyTab = location.pathname === ROUTES.RECEIPT_IMAGE_TAB;

  const isPaletteOpen = useCommandPaletteStore((s) => s.isOpen);
  const closePalette = useCommandPaletteStore((s) => s.close);

  return (
    <ErrorBoundary>
      {!isReceiptImageOnlyTab && (
        <>
          <ScrollToTopButton />
          <PageTitle />
          <CommandPalette isOpen={isPaletteOpen} onClose={closePalette} />
        </>
      )}
      <AppToaster />
      <RouteChunkErrorBoundary>
        <Suspense fallback={<AppSuspenseFallback />}>
          <Routes>
            <Route
              path={ROUTES.COMING_SOON_HTML}
              element={<ComingSoonStandalone />}
            />
            <Route
              path={ROUTES.RECEIPT_IMAGE_TAB}
              element={<ReceiptImageTab />}
            />
            <Route path={ROUTES.AUTH_CALLBACK} element={<AuthCallback />} />
            <Route path={ROUTES.HOME} element={<Home />} />
            <Route
              path={ROUTES.TAX_CALCULATOR}
              element={<PublicTaxCalculator />}
            />
            <Route
              path={ROUTES.VERIFY_RECEIPT_PATH}
              element={<VerifyPosReceipt />}
            />
            <Route
              path={ROUTES.VERIFY_AR_INVOICE_PATH}
              element={<VerifyArInvoice />}
            />
            <Route path={ROUTES.SUPPORT} element={<SupportPage />} />
            <Route
              path={ROUTES.SHOP.MANAGE.ROOT}
              element={<ShopManageLayout />}
            >
              <Route index element={<ManageShop />} />
              <Route path="orders" element={<ShopOrdersPage />} />
              <Route
                path="orders/:orderNumber"
                element={<ShopOrderDetailPage />}
              />
            </Route>
            {/* Public online-shop surfaces share one unified guest cart. */}
            <Route element={<GuestCartLayout />}>
              <Route path={ROUTES.SHOP.ROOT} element={<OnlineShop />} />
              <Route path={ROUTES.SHOP.CART} element={<GuestCart />} />
              <Route path={ROUTES.SHOP.CHECKOUT} element={<GuestCheckout />} />
              <Route path={ROUTES.SHOP.ORDERS} element={<GuestOrders />} />
              <Route
                path={ROUTES.SHOP.ORDER_DETAIL_PATH}
                element={<StorefrontOrderDetail />}
              />
              <Route
                path={ROUTES.SHOP.ORDER_SUCCESS_PATH}
                element={<StorefrontOrderSuccess />}
              />
              <Route
                path={ROUTES.SHOP.PRODUCT_PATH}
                element={<StorefrontProductDetail />}
              />
              <Route
                path={ROUTES.SHOP.STOREFRONT_PATH}
                element={<ShopStorefront />}
              >
                <Route index element={<StorefrontProductList />} />
                <Route
                  path="cart"
                  element={<Navigate to={ROUTES.SHOP.CART} replace />}
                />
                <Route path="orders" element={<StorefrontOrderHistory />} />
              </Route>
            </Route>
            <Route path={ROUTES.LEGAL.PRIVACY} element={<PrivacyPolicy />} />
            <Route
              path={ROUTES.LEGAL.NPC_CERTIFICATE}
              element={<NpcCertificateViewer />}
            />
            <Route path={ROUTES.LEGAL.TERMS} element={<TermsAndConditions />} />
            <Route path={ROUTES.LEGAL.COOKIES} element={<CookiePolicy />} />
            <Route path={ROUTES.LEGAL.DISCLAIMER} element={<Disclaimer />} />
            <Route
              path={ROUTES.EVENTS.PUBLIC_REGISTER}
              element={<EventRegistrationPage />}
            />
            <Route
              path={ROUTES.EVENTS.PUBLIC_TICKET}
              element={<EventTicketPage />}
            />
            <Route
              path={ROUTES.EVENTS.PUBLIC_SCANNER}
              element={<EventScannerPage />}
            />
            <Route
              path={ROUTES.EVENTS.PUBLIC_SURVEY}
              element={<EventSurveyPage />}
            />
            <Route
              path={ROUTES.PUBLIC_RESERVE.CAMERA_CAPTURE}
              element={<PublicReserveCameraCapture />}
            />
            <Route
              path={ROUTES.PUBLIC_RESERVE.AUTHORIZED_SCAN_CAPTURE}
              element={<PublicReserveAuthorizedScanCapture />}
            />
            <Route
              path={ROUTES.PUBLIC_RESERVE.ROOT}
              element={<PublicReserveLayout />}
            >
              <Route index element={<PublicPlacesList />} />
              <Route path="places/:placeId" element={<PublicPlaceBook />} />
              <Route
                path="places/:placeId/book"
                element={<PublicPlaceBook />}
              />
              <Route
                path="confirmation/:bookingReference"
                element={<PublicConfirmation />}
              />
              <Route path="lookup" element={<PublicLookup />} />
            </Route>
            {[ROUTES.INSTOREQR.MENU, ROUTES.INSTOREQR.MENU_UID].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <Suspense fallback={<InstoreqrLoadingPage />}>
                    <CustomerMenu />
                  </Suspense>
                }
              />
            ))}
            <Route path="/*" element={<AuthenticatedRoutes />} />
          </Routes>
        </Suspense>
      </RouteChunkErrorBoundary>
    </ErrorBoundary>
  );
}

export default App;
